import { useTheme } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMemo } from "react";
import dayjs from "dayjs";
import { ConfirmModal } from "@/shared/components/confirm-modal";
import { MedicationCard } from "../../../medbox/components/medications/medications-list/card";
import { Tooltip } from '@/shared/ui';
import { CustomersController } from "@/shared/endpoints/customers";
import { ITestClaimSlot } from "@/shared/helpers/test-claims-combo";
import { useAppDispatch } from "@/shared/hooks/store";
import { memo, useRef, useState } from "react";
import { ITestClaimTransferPrescriptions, ITransferPrescriptions } from "@/shared/types/interfaces/transfer-prescriptions";
import BinDark from "@/assets/icons/bin-dark.svg";
import { MedicalCardEditQuantity } from "../medical-card-edit-quantity";
import { IMedicationQuantityInitialValues } from "../../../../../../shared/constants/medication-quantity";
import Pen from '@/assets/icons/pen.svg';
import './style.css';
import { getMedicationQuantityLabel } from "@/shared/helpers/medication-quantity";
import AlertWarningIcon from '@/assets/icons/alert_warning.svg';
import AlertSuccessIcon from '@/assets/icons/alert_success.svg';
import { useHoverSupport } from '@/shared/hooks/useHoverSupport';
import Loading from '@/shared/ui/loading';

type TooltipSource = 'copay_error' | 'otc_price_fetch_error' | 'copay_details_pre_req' | 'copay_details_post_req';
const MedicalCard = ({
    medication,
    claim,
    otcCost,
    onQuantityChange,
    hasEditQuantity,
    deletionModalText,
    copayAmount = null,
    showCopay,
    copayRequestsExhausted,
    claimsRunning
}: {
    medication: ITestClaimTransferPrescriptions;
    claim?: ITestClaimSlot;
    otcCost?: any;
    onQuantityChange?: (values: IMedicationQuantityInitialValues) => void;
    hasEditQuantity?: boolean;
    deletionModalText?: string;
    copayAmount?: number | null;
    shouldShowEditQuantityTooltip?: boolean;
    showCopay?: boolean;
    copayRequestsExhausted?: boolean;
    claimsRunning?: boolean;
}) => {
    const theme = useTheme();
    const isBelowXl = useMediaQuery(theme.breakpoints.down('xl'));
    const copayDetailsPreRef = useRef<HTMLSpanElement | null>(null);
    const copayDetailsPostRef = useRef<HTMLSpanElement | null>(null);
    const copayUnavailableRef = useRef<HTMLSpanElement | null>(null);
    const dispatch = useAppDispatch();
    const [deleteModalShown, setDeleteModalShown] = useState(false);
    const [editMedication, setEditMedication] = useState<ITransferPrescriptions | null>(null);
    const [tooltipState, setTooltipState] = useState<{
        source: TooltipSource;
        clickOpen: boolean;
    } | null>(null);

    const canHover = useHoverSupport();

    const deleteMedication = () => {
        dispatch(CustomersController.removeTransferPrescription(medication));
        setDeleteModalShown(false);
    };

    const relevantRef = useMemo(() => {
        if (tooltipState?.source === 'copay_details_pre_req') {
            return copayDetailsPreRef;
        } else if (tooltipState?.source === 'copay_details_post_req') {
            return copayDetailsPostRef;
        } else {
            return copayUnavailableRef;
        }
    }, [tooltipState, copayDetailsPreRef, copayDetailsPostRef, copayUnavailableRef]); // update the ref

    const fillTooSoon = useMemo(() => {
        if (!claim?.rejectCodes?.includes('FILL_TOO_SOON')) return null;
        if (!claim.retryNotBefore) return null;
        return {
            fillTooSoonDate: dayjs(claim.retryNotBefore, 'YYYY-MM-DD').format('MM/DD/YYYY')
        };
    }, [claim]);

    const copayOrPriceLocal = useMemo(() => {
        let tag = 'copay';
        let cost = null;
        if (medication.otc) {
            tag = 'price';
            cost = otcCost?.cost ?? null;
        } else {
            cost = copayAmount ?? null;
        }
        return {
            cost,
            tag
        };
    }, [medication, otcCost, copayAmount]);

    const rejectionDueToFillTooAoon = useMemo(() => {
        if (!claim?.rejectCodes?.length) {
            return false;
        }
        const noFillTooSoonRejectCode = claim?.rejectCodes?.includes('FILL_TOO_SOON');
        return noFillTooSoonRejectCode;
    }, [claim]);


    const rejection = useMemo<"FILL_TOO_SOON" | "NOT_AVAILABLE" | null>(() => {
        const medicationOtcWithError = medication.otc && copayOrPriceLocal.cost === null;
        const copayError = claim?.rejectCodes?.length || claim?.status === 'ERROR' || copayRequestsExhausted;
        if (copayOrPriceLocal.cost !== null || showCopay === false) {
            return null;
        } else if (
            rejectionDueToFillTooAoon
        ) {
            return 'FILL_TOO_SOON';
        } else if (copayError || medicationOtcWithError) {
            return 'NOT_AVAILABLE';
        }
        return null;
    }, [
        claim?.rejectCodes?.length,
        claim?.status,
        copayOrPriceLocal.cost,
        copayRequestsExhausted,
        medication.otc,
        rejectionDueToFillTooAoon,
        showCopay
    ]);


    const canShowQuantity = useMemo(() => {
        return !!medication.copayQuantity || !!medication.quantity;
    }, [medication])

    const canShowEditButton = useMemo(() => {
        return canShowQuantity && hasEditQuantity && showCopay && !medication.otc
    }, [canShowQuantity, hasEditQuantity, medication.otc, showCopay]);

    const subMessage = useMemo(() => {
        if (rejection) {
            if (medication.otc) {
                return "This price is currently unavailable. Please connect with our team through chat to find out more.";
            } else if (rejection === 'FILL_TOO_SOON') {
                return "To find out sooner, check with your insurance.";
            }
            return null;
        }
    }, [medication.otc, rejection]);

    const failedCopayRetrievalInformationalBanner = useMemo(() => {
        if (rejection) {
            return (
                <div className="inline-flex flex-col gap-[0.626rem] cursor-pointer">
                    <span
                        className="inline-flex items-center w-fit text-dark font-dmSansBold font-bold text-15 rounded-full pl-[0.375rem] pr-2.5 py-[0.4375rem] bg-yellow-100 border border-yellow-300"
                        onMouseEnter={() => {
                            if (!tooltipState?.clickOpen && canHover) {
                                setTooltipState({
                                    source: medication?.otc ? 'otc_price_fetch_error' : 'copay_error',
                                    clickOpen: false
                                });
                            }
                        }}
                        onMouseLeave={() => {
                            if (!tooltipState?.clickOpen && tooltipState?.source && canHover) {
                                setTooltipState(null);
                            }
                        }}
                        ref={(ref) => {
                            if (isBelowXl) {
                                return;
                            } else {
                                copayUnavailableRef.current = ref;
                            }
                        }}
                    >
                        <img
                            onClick={() => {
                                if (!canHover) {
                                    setTooltipState({
                                        source: medication?.otc ? 'otc_price_fetch_error' : 'copay_error',
                                        clickOpen: true
                                    });
                                }
                            }}
                            ref={(ref) => {
                                if (isBelowXl) {
                                    copayUnavailableRef.current = ref;
                                } else {
                                    return;
                                }
                            }}
                            width={17}
                            className="w-[1.0625rem] mr-[0.4375rem] flex-shrink-0"
                            src={AlertWarningIcon}
                            alt="Warning"
                        />
                        {medication?.otc
                            ? 'Price Not Available'
                            : rejection === 'FILL_TOO_SOON'
                                ? 'Copay Not yet Available' : 'Copay Not Available'}
                    </span>
                    {subMessage && (
                        <span className="font-dmSans text-16 font-medium leading-[125%]">
                            {subMessage}
                        </span>
                    )}
                </div>
            )
        }
        return null;
    }, [
        canHover,
        isBelowXl,
        medication?.otc,
        rejection,
        subMessage,
        tooltipState?.clickOpen,
        tooltipState?.source
    ]);

    const priceUi = useMemo(() => {
        if (claimsRunning) {
            return <Loading size="extra-small" className="justify-end" />;
        }
        if (showCopay) {
            if (fillTooSoon) {
                return (
                    <span className="inline-block text-16 font-dmSansBold font-bold md:text-right bg-primary-1200 rounded-md p-[5px] leading-[125%] md:min-w-[172px]">
                        Will be available{' '}
                        <span className="md:block italic">
                            {fillTooSoon.fillTooSoonDate}
                        </span>
                    </span>
                )
            } else {
                return (
                    <span className="inline-block text-24 font-dmSansBold font-bold text-left md:text-right leading-[125%]">
                        {copayOrPriceLocal?.cost != null
                            ? `$${copayOrPriceLocal.cost.toFixed(2)}`
                            : (
                                <>
                                    <span className="md:hidden">$--</span>
                                    <span className="hidden md:inline">-</span>
                                </>
                            )}
                    </span>
                )
            }
        }
    }, [claimsRunning, copayOrPriceLocal?.cost, fillTooSoon, showCopay]);

    const daysSupplyIs90 = useMemo(() => {
        return (medication.copayDaysSupply || 30) === 90;
    }, [medication.copayDaysSupply]);

    const toolTipContents = useMemo(() => {
        const tooltipMap = {
            copay_details_post_req: {
                icon: AlertSuccessIcon,
                alt: 'Success',
                title: 'How your copay was calculated',
                text: 'This copay may differ from what you’re used to, as it’s calculated for a 30-day supply instead of 90 days. MedBox provides 30-day supplies only, and your copay reflects that change.'
            },
            copay_details_pre_req: {
                icon: AlertSuccessIcon,
                alt: 'Success',
                title: 'Updated to 30-day refills',
                text: 'MedBox provides 30-day supplies only. Your prescription will now be filled and shipped monthly.'
            },
            copay_error: {
                icon: AlertWarningIcon,
                alt: 'Warning',
                title: 'Oops! We couldn’t fetch your copay',
                text: `Don’t worry. Copays usually stay the same as at a retail pharmacy for a 30-day supply. You can also confirm the exact amount with your insurance provider by calling the number on your insurance card. Copays are set by your insurance, not by the pharmacy.`
            },
            otc_price_fetch_error: {
                icon: AlertWarningIcon,
                alt: 'Warning',
                title: 'Oops! We couldn’t load the price',
                text: `Please reach out to our care team via chat in the lower right hand corner to find out more.`
            }
        } as const;

        const content = tooltipMap[tooltipState?.source as keyof typeof tooltipMap];
        if (!content) return null;

        return (
            <div className="flex flex-col gap-[7px]">
                <div className="flex flex-row">
                    <img
                        width={20}
                        height={20}
                        className="w-[1.0625rem] mr-[0.4375rem] flex-shrink-0"
                        src={content.icon}
                        alt={content.alt}
                    />
                    <span className="font-atkinsonBold text-20 font-bold leading-[120%]">{content.title}</span>
                </div>
                <span className="block font-atkinsonHyperlegible text-18 leading-[133%]">{content.text}</span>
            </div>
        );
    }, [tooltipState]);

    const toolTipColors = useMemo(() => {
        if (tooltipState?.source === 'copay_details_post_req' || tooltipState?.source === 'copay_details_pre_req') {
            return {
                squareColor: "!bg-mint-200",
                squareBorderColor: "border-primary-700",
                classNames: "!bg-mint-200 border-1 border-primary-700"
            }
        } else if (tooltipState?.source === 'copay_error' || tooltipState?.source === 'otc_price_fetch_error') {
            return {
                squareColor: "bg-yellow-100",
                squareBorderColor: "border-yellow-300",
                classNames: "!bg-yellow-100 border-1 border-yellow-300"
            }
        }
        return {
            className: "",
            squareColor: "",
            squareBorderColor: ""
        }
    }, [tooltipState]);




    const toolTipVariant = useMemo(() => {
        if (isBelowXl) {
            return 'top-full';
        }
        return 'right';
    }, [isBelowXl]);

    return (
        <>
            <div className="w-full flex flex-col flex-wrap justify-between items-end rounded-[15px] shadow-reviewMedicationCard">
                <div className={`w-full flex flex-col gap-[0.625rem] rounded-t-[15px] p-[20px] ${(showCopay || !canShowQuantity) ? '' : 'pb-0'}`}>
                    <div className="flex flex-row justify-between w-full items-center">
                        <span className="ph-no-capture font-atkinsonBold font-bold text-18 sm:text-20 uppercase leading-[111%] sm:leading-[140%] overflow-hidden text-ellipsis">{medication.prescribable.name}</span>
                        <span
                            className="bg-mint-600 rounded-full cursor-pointer flex items-center justify-center min-w-[2.25rem] max-w-[2.25rem] h-[2.25rem] ml-3"
                            onClick={() => setDeleteModalShown(true)}
                        >
                            <img width="13px" className="w-[0.8125rem]" src={BinDark} alt="delete" />
                        </span>
                    </div>
                    {failedCopayRetrievalInformationalBanner}
                </div>
                {canShowQuantity &&
                    <div className={`${showCopay ? 'bg-mint-100' : ''} w-full rounded-b-[15px]`}>
                        <div className="p-[20px]">
                            <div className="flex flex-col-reverse md-max:gap-[25px] sm:flex-row justify-between gap-2 font-dmSansBold text-14 font-bold uppercase">
                                <div className="flex flex-col flex-start ms:flex-row gap-[20px]">
                                    <div className="flex flex-col gap-[10px] leading-[105%]">
                                        <div className="flex flex-row gap-[3px]">
                                            <span className="block text-information">day supply</span>
                                            {daysSupplyIs90 && (!showCopay || (showCopay && copayOrPriceLocal.cost === null)) &&
                                                <img ref={copayDetailsPreRef} onClick={() => {
                                                    if (!canHover) {
                                                        setTooltipState({
                                                            source: 'copay_details_pre_req',
                                                            clickOpen: true
                                                        });
                                                    }
                                                }}
                                                    onMouseEnter={() => {
                                                        if (!tooltipState?.clickOpen && canHover) {
                                                            setTooltipState({
                                                                source: 'copay_details_pre_req',
                                                                clickOpen: false
                                                            });
                                                        }
                                                    }}
                                                    onMouseLeave={() => {
                                                        if (!tooltipState?.clickOpen && tooltipState?.source && canHover) {
                                                            setTooltipState(null);
                                                        }
                                                    }}
                                                    className="cursor-pointer" width={13} height={13} src={AlertSuccessIcon} alt="day supply" />
                                            }
                                        </div>
                                        <span className="block text-16">30 days</span>
                                    </div>
                                    <div className="flex flex-col gap-[10px] leading-[105%]">
                                        <span className="block text-information">quantity</span>
                                        <div className="flex flex-row flex-nowrap items-center justify-start">
                                            <span className="block text-16">{getMedicationQuantityLabel(medication)}</span>
                                            {canShowEditButton && <button
                                                onClick={() => setEditMedication(medication)}
                                                className="ml-2 text-primary flex flex-row flex-nowrap items-center font-dmSansBold text-18 leading-[105%]">
                                                <img height={13} width={13} src={Pen} alt="edit" />
                                                <span className="ml-1 text-18">Edit</span>
                                            </button>}
                                        </div>
                                    </div>
                                </div>
                                {showCopay && (
                                    <div className="inline-flex flex-col normal-case w-fit leading-[105%] gap-[5px]">
                                        <div className="flex justify-end sm-max:mr-auto flex-row gap-[3px]">
                                            <span className="block text-information">
                                                {medication?.otc ? 'SELF PAY' : 'COPAY'}
                                            </span>
                                            {daysSupplyIs90 && copayOrPriceLocal.cost !== null &&
                                                <img
                                                    ref={copayDetailsPostRef}
                                                    onClick={() => {
                                                        if (!canHover) {
                                                            setTooltipState({
                                                                source: 'copay_details_post_req',
                                                                clickOpen: true
                                                            });
                                                        }
                                                    }}
                                                    onMouseEnter={() => {
                                                        if (!tooltipState?.clickOpen && canHover) {
                                                            setTooltipState({
                                                                source: 'copay_details_post_req',
                                                                clickOpen: false
                                                            });
                                                        }
                                                    }}
                                                    onMouseLeave={() => {
                                                        if (!tooltipState?.clickOpen && tooltipState?.source && canHover) {
                                                            setTooltipState(null);
                                                        }
                                                    }}
                                                    className="cursor-pointer" width={13} height={13} src={AlertSuccessIcon} alt="day supply" />}
                                        </div>
                                        {priceUi}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                }
                <Tooltip
                    customPosition={`${toolTipVariant}`}
                    wrapperClassName=""
                    className={`!max-w-[475px] p-[10px] ${toolTipColors.classNames}`}
                    open={!!tooltipState?.source}
                    anchorEl={relevantRef?.current}
                    squareColor={toolTipColors.squareColor}
                    squareBorderColor={toolTipColors.squareBorderColor}
                    onClose={() => setTooltipState(null)}
                    hasCloseIcon={tooltipState?.clickOpen}
                    content={toolTipContents}
                >
                    {toolTipContents}
                </Tooltip>
            </div>
            {editMedication && (
                <MedicalCardEditQuantity
                    onClose={() => setEditMedication(null)}
                    onSubmit={value => { onQuantityChange?.(value); setEditMedication(null); }}
                    medication={editMedication}
                />
            )}

            <ConfirmModal
                open={!!deleteModalShown}
                onCancel={() => setDeleteModalShown(false)}
                onConfirm={deleteMedication}
                title={deletionModalText || 'Are you sure you want to remove this medication?'}
                confirmText="Yes, Remove"
                cancelText="Cancel Removal"
            >
                <MedicationCard medication={medication} showActions={false} />
            </ConfirmModal>
        </>
    );
};

export default memo(MedicalCard);